import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import cogoToast from "cogo-toast";
import {
  createWorkforcePlanningApi,
  getWorkforcePlanningDetailsApi,
  listOfficesApi,
  listStaffDesignationsApi,
  updateWorkforcePlanningApi,
} from "../../../services/Api";
import { DEFAULT_RESULT, SUCCESS_RESULT } from "../../../Constants";
import ability from "../../config/defineAbility";
import { PERMS } from "../../config/perms";
import {
  AUTO_SCORE_MAX,
  EMPTY_BUDGET_ENTRY,
  EMPTY_FORM,
  RATING_OPTIONS_1_TO_3,
  EVIDENCE_RATING_OPTIONS_1_TO_3,
  ROLE_MERGED_OR_SHARED_OPTIONS,
  buildPayload,
  computeAutoScores,
  isBudgetEntryComplete,
  isWorkforcePlanningCompleted,
  recordToForm,
  withDerivedGapsAndScore,
} from "./workforcePlanningHelpers";
import "./WorkforcePlanningForm.css";
import { FaPlus, FaTrash } from "react-icons/fa";

const STEPS = [
  {
    key: "role",
    title: "Role & Location",
    description: "Choose the designation, area and office for this request.",
  },
  {
    key: "staffing",
    title: "Staffing Needs",
    description: "Capture current headcount, needs and gap/surplus figures.",
  },
  {
    key: "budget",
    title: "Budget",
    description: "Add salary, available budget and funding confirmation details.",
  },
  {
    key: "justification",
    title: "Justification",
    description:
      "Rate evidence and risk (1–3), explain each answer, and say whether the role can be merged.",
  },
  {
    key: "scoring",
    title: "Recommendations",
    description: "",
  },
];

const Field = ({ label, children, hint }) => (
  <FormGroup>
    <Label className="wfp-label">{label}</Label>
    {children}
    {hint ? <div className="wfp-hint">{hint}</div> : null}
  </FormGroup>
);

const SELECT_MENU_PORTAL_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  },
};

const AREA_OPTIONS = [
  { value: "SC", label: "SC" },
  { value: "PL", label: "PL" },
  { value: "SL", label: "SL" },
];

const officeMatchesArea = (officeArea, selectedArea) => {
  if (!selectedArea) return true;
  if (!officeArea) return false;
  const parts = String(officeArea)
    .split("/")
    .map((p) => p.trim().toUpperCase())
    .filter(Boolean);
  return parts.includes(String(selectedArea).trim().toUpperCase());
};

const WorkforcePlanningForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const history = useHistory();
  const canCreate = ability.can(PERMS.CREATE, PERMS.CAN_CREATE_WORKFORCE_PLANNING);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    budget_details: [{ ...EMPTY_BUDGET_ENTRY }],
  });
  const [roles, setRoles] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingOffices, setLoadingOffices] = useState(true);
  const [budgetDecisionMade, setBudgetDecisionMade] = useState(false);
  const [draftId, setDraftId] = useState(id || null);
  const [staffingModal, setStaffingModal] = useState(false);
  const [staffingInputs, setStaffingInputs] = useState({ stable_staff_needed: "", growth_staff_needed: "" });
  const [fieldErrors, setFieldErrors] = useState(new Set());

  useEffect(() => {
    if (!isEdit && !canCreate) {
      cogoToast.error("You do not have permission to create workforce planning records.", {
        hideAfter: 4,
      });
      history.replace("/hrm/workforce-planning");
    }
  }, [isEdit, canCreate, history]);

  const getRoleDepartmentId = (role) =>
    role?.department_id ||
    role?.department?.id ||
    "";

  const getRoleDepartmentName = (role) =>
    role?.department_name ||
    role?.department?.department_name ||
    role?.department?.name ||
    "";

  const departmentOptions = useMemo(() => {
    const map = new Map();
    (roles || []).forEach((role) => {
      const id = getRoleDepartmentId(role);
      const name = getRoleDepartmentName(role);
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          value: id,
          label: name || id,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [roles]);

  const roleOptions = useMemo(
    () =>
      (roles || [])
        .filter((r) => {
          if (!form.department_id) return false;
          return getRoleDepartmentId(r) === form.department_id;
        })
        .map((r) => ({
          value: r.id,
          label: r.role_name || r.name || r.id,
          department_id: getRoleDepartmentId(r),
        })),
    [roles, form.department_id]
  );

  const officeOptions = useMemo(
    () =>
      (offices || [])
        .filter((o) => officeMatchesArea(o.area, form.area))
        .map((o) => {
          const name = o.office_name || o.name || "";
          return {
            value: name,
            label: name || `Office ${o.id}`,
            area: o.area || "",
          };
        })
        .filter((o) => o.value),
    [offices, form.area]
  );

  const selectedDepartment =
    departmentOptions.find((o) => o.value === form.department_id) || null;
  const selectedRole = roleOptions.find((o) => o.value === form.role_id) || null;
  const selectedArea =
    AREA_OPTIONS.find((o) => o.value === form.area) ||
    (form.area ? { value: form.area, label: form.area } : null);
  const selectedOffice =
    officeOptions.find((o) => o.value === form.office) ||
    (form.office ? { value: form.office, label: form.office } : null);

  const roleLabel = selectedRole?.label || "Not selected";
  const departmentLabel = selectedDepartment?.label || "Not selected";
  const officeLabel = selectedOffice?.label || form.office || "Not selected";

  useEffect(() => {
    if (id) setDraftId(id);
  }, [id]);

  useEffect(() => {
    loadRoles();
    loadOffices();
    if (isEdit) {
      loadDetails(id);
    }
  }, [id, isEdit]);

  // When roles load on edit, fill department from the selected role if missing
  useEffect(() => {
    if (!form.role_id || form.department_id || !roles.length) return;
    const matched = roles.find((r) => r.id === form.role_id);
    const deptId = getRoleDepartmentId(matched);
    if (deptId) {
      setForm((prev) => ({ ...prev, department_id: deptId }));
    }
  }, [roles, form.role_id, form.department_id]);

  const handleDepartmentChange = (opt) => {
    const nextDepartmentId = opt?.value || "";
    setForm((prev) => {
      const currentRole = (roles || []).find((r) => r.id === prev.role_id);
      const roleStillValid =
        currentRole && getRoleDepartmentId(currentRole) === nextDepartmentId;
      return withDerivedGapsAndScore({
        ...prev,
        department_id: nextDepartmentId,
        role_id: roleStillValid ? prev.role_id : "",
        critically_need_role: "",
      });
    });
    setFieldErrors((prev) => { const n = new Set(prev); n.delete("department_id"); return n; });
  };

  const loadOffices = () => {
    setLoadingOffices(true);
    listOfficesApi()
      .then((res) => {
        setLoadingOffices(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const data = res.data.data;
          setOffices(Array.isArray(data) ? data : []);
        } else {
          setOffices([]);
          cogoToast.error(res?.data?.message || "Failed to load offices", {
            hideAfter: 4,
          });
        }
      })
      .catch((err) => {
        setLoadingOffices(false);
        setOffices([]);
        cogoToast.error(
          err?.response?.data?.message || "Failed to load offices",
          { hideAfter: 4 }
        );
      });
  };

  const loadRoles = () => {
    setLoadingRoles(true);
    listStaffDesignationsApi({})
      .then((res) => {
        setLoadingRoles(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const data = res.data.data;
          setRoles(Array.isArray(data) ? data : []);
        } else {
          setRoles([]);
          cogoToast.error(res?.data?.message || "Failed to load designations", {
            hideAfter: 4,
          });
        }
      })
      .catch((err) => {
        setLoadingRoles(false);
        setRoles([]);
        cogoToast.error(
          err?.response?.data?.message || "Failed to load designations",
          { hideAfter: 4 }
        );
      });
  };

  const loadDetails = (recordId) => {
    setLoading(true);
    getWorkforcePlanningDetailsApi({ id: recordId })
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const record = res.data.data || {};
          if (isEdit && isWorkforcePlanningCompleted(record.status)) {
            cogoToast.error("Completed records cannot be edited.", { hideAfter: 4 });
            history.replace(`/hrm/workforce-planning/details/${recordId}`);
            return;
          }
          const nextForm = recordToForm(record);
          setForm(withDerivedGapsAndScore(nextForm));
          setBudgetDecisionMade(true);
          if (record.role?.id) {
            setRoles((prev) => {
              if (prev.some((r) => r.id === record.role.id)) return prev;
              return [...prev, record.role];
            });
          }
        } else {
          cogoToast.error(res.data?.message || "Failed to load record", { hideAfter: 5 });
          history.push("/hrm/workforce-planning");
        }
      })
      .catch((err) => {
        setLoading(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading the record.",
          { hideAfter: 5 }
        );
        history.push("/hrm/workforce-planning");
      });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setForm((prev) => withDerivedGapsAndScore({ ...prev, [name]: nextValue }));
    if (name) {
      setFieldErrors((prev) => {
        if (!prev.has(name)) return prev;
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  const handleHasBudgetChange = (e) => {
    const checked = e.target.checked;
    setBudgetDecisionMade(true);
    if (checked) {
      setForm((prev) =>
        withDerivedGapsAndScore({
          ...prev,
          has_budget_for_role: true,
          budget_details:
            !prev.budget_details || prev.budget_details.length === 0
              ? [{ ...EMPTY_BUDGET_ENTRY }]
              : prev.budget_details,
        })
      );
      return;
    }
    setForm((prev) =>
      withDerivedGapsAndScore({
        ...prev,
        has_budget_for_role: false,
        monthly_gross_salary: "",
        nbr_of_months: "",
        total_salary: "",
        available_budget: 0,
        budget_surplus_gap: "",
        budget_details: [{ ...EMPTY_BUDGET_ENTRY }],
      })
    );
  };

  const skipBudgetAndContinue = () => {
    const nextForm = withDerivedGapsAndScore({
      ...form,
      has_budget_for_role: false,
      monthly_gross_salary: "",
      nbr_of_months: "",
      total_salary: "",
      available_budget: 0,
      budget_surplus_gap: "",
      budget_details: [{ ...EMPTY_BUDGET_ENTRY }],
    });
    setForm(nextForm);
    setBudgetDecisionMade(true);
    saveProgress({
      formOverride: nextForm,
      budgetDecisionOverride: true,
      advanceTo: 3,
    });
  };

  const handleBudgetEntryChange = (index, field, value) => {
    setForm((prev) => {
      const budget_details = (prev.budget_details || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      return withDerivedGapsAndScore({ ...prev, budget_details });
    });
  };

  const addBudgetEntry = () => {
    setForm((prev) =>
      withDerivedGapsAndScore({
        ...prev,
        budget_details: [...(prev.budget_details || []), { ...EMPTY_BUDGET_ENTRY }],
      })
    );
  };

  const removeBudgetEntry = (index) => {
    setForm((prev) => {
      const current = prev.budget_details || [];
      const budget_details =
        current.length <= 1
          ? [{ ...EMPTY_BUDGET_ENTRY }]
          : current.filter((_, i) => i !== index);
      return withDerivedGapsAndScore({ ...prev, budget_details });
    });
  };

  const handleAreaChange = (opt) => {
    const nextArea = opt?.value || "";
    setForm((prev) => {
      const officeStillValid =
        prev.office &&
        (offices || []).some(
          (o) =>
            (o.office_name || o.name) === prev.office &&
            officeMatchesArea(o.area, nextArea)
        );
      return withDerivedGapsAndScore({
        ...prev,
        area: nextArea,
        office: officeStillValid ? prev.office : "",
        critically_need_role: "",
      });
    });
    setFieldErrors((prev) => { const n = new Set(prev); n.delete("area"); n.delete("office"); return n; });
  };

  const handleOfficeChange = (opt) => {
    setForm((prev) =>
      withDerivedGapsAndScore({
        ...prev,
        office: opt?.value || "",
        critically_need_role: "",
      })
    );
    setFieldErrors((prev) => { const n = new Set(prev); n.delete("office"); return n; });
  };

  const isFilled = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

  const errCls = (key) =>
    fieldErrors.has(key) ? "wfp-input wfp-field-error" : "wfp-input";

  const isValidNumber = (value) => {
    if (!isFilled(value)) return false;
    return !Number.isNaN(Number(value));
  };

  const getStepMissingFieldKeys = (index, formState = form) => {
    const checks = [
      [
        ["department_id", "Department", () => isFilled(formState.department_id)],
        ["role_id", "Role / designation", () => isFilled(formState.role_id)],
        ["area", "Area", () => isFilled(formState.area)],
        ["office", "Office", () => isFilled(formState.office)],
      ],
      [
        ["current_staff_count", "Current staff count", () => isValidNumber(formState.current_staff_count)],
        ["minimum_staff_needed", "Minimum needed", () => isValidNumber(formState.minimum_staff_needed)],
        ["stable_staff_needed", "Stable needed", () => isValidNumber(formState.stable_staff_needed)],
        ["growth_staff_needed", "Growth needed", () => isValidNumber(formState.growth_staff_needed)],
        ["minimum_gap_surplus", "Minimum gap / surplus", () => isValidNumber(formState.minimum_gap_surplus)],
        ["stable_gap_surplus", "Stable gap / surplus", () => isValidNumber(formState.stable_gap_surplus)],
        ["growth_gap_surplus", "Growth gap / surplus", () => isValidNumber(formState.growth_gap_surplus)],
      ],
      [
        ...(formState.has_budget_for_role
          ? [
              [
                "monthly_gross_salary",
                "Monthly gross salary",
                () => isValidNumber(formState.monthly_gross_salary),
              ],
              ["nbr_of_months", "Number of months", () => isValidNumber(formState.nbr_of_months)],
              ["total_salary", "Total salary", () => isValidNumber(formState.total_salary)],
              [
                "budget_details",
                "Budget entries",
                () => {
                  const rows = formState.budget_details || [];
                  if (!rows.some(isBudgetEntryComplete)) return false;
                  return rows.every((row) => {
                    const started =
                      String(row.project_name || "").trim() !== "" ||
                      String(row.budget_line || "").trim() !== "" ||
                      (row.amount !== "" && row.amount != null) ||
                      String(row.budget_confirmed_until || "").trim() !== "";
                    return !started || isBudgetEntryComplete(row);
                  });
                },
              ],
              [
                "available_budget",
                "Available budget",
                () => isValidNumber(formState.available_budget),
              ],
            ]
          : []),
      ],
      [
        ["why_role_is_needed", "Why role is needed", () => isFilled(formState.why_role_is_needed)],
        [
          "evidence_reference_score",
          "Evidence reference score (1–3)",
          () => {
            const n = Number(formState.evidence_reference_score);
            return !Number.isNaN(n) && n >= 1 && n <= 3;
          },
        ],
        ["evidence_reference", "Evidence reference", () => isFilled(formState.evidence_reference)],
        [
          "risk_if_role_is_not_available_score",
          "Risk rating (1–3)",
          () => {
            const n = Number(formState.risk_if_role_is_not_available_score);
            return !Number.isNaN(n) && n >= 1 && n <= 3;
          },
        ],
        [
          "risk_if_role_is_not_available",
          "Risk if role is not available",
          () => isFilled(formState.risk_if_role_is_not_available),
        ],
        [
          "can_this_role_be_merged_or_shared",
          "Can this role be merged or shared",
          () =>
            formState.can_this_role_be_merged_or_shared === "Yes" ||
            formState.can_this_role_be_merged_or_shared === "No",
        ],
        [
          "merged_or_shared_details",
          formState.can_this_role_be_merged_or_shared === "Yes"
            ? "Which other role can be merged"
            : formState.can_this_role_be_merged_or_shared === "No"
            ? "Why it cannot be merged"
            : "Merge / share explanation",
          () => isFilled(formState.merged_or_shared_details),
        ],
      ],
      [
        ["total_score", "Total score", () => isValidNumber(formState.total_score)],
        ["criticality_category", "Criticality", () => isFilled(formState.criticality_category)],
        [
          "hod_requested_action_recommendation",
          "HoD Requested Action / Recommendation",
          () => isFilled(formState.hod_requested_action_recommendation),
        ],
      ],
    ];
    return (checks[index] || []).filter(([, , ok]) => !ok()).map(([key]) => key);
  };

  const getStepMissingFields = (index, formState = form) => {
    const checks = [
      [
        ["department_id", "Department", () => isFilled(formState.department_id)],
        ["role_id", "Role / designation", () => isFilled(formState.role_id)],
        ["area", "Area", () => isFilled(formState.area)],
        ["office", "Office", () => isFilled(formState.office)],
      ],
      [
        ["current_staff_count", "Current staff count", () => isValidNumber(formState.current_staff_count)],
        ["minimum_staff_needed", "Minimum needed", () => isValidNumber(formState.minimum_staff_needed)],
        ["stable_staff_needed", "Stable needed", () => isValidNumber(formState.stable_staff_needed)],
        ["growth_staff_needed", "Growth needed", () => isValidNumber(formState.growth_staff_needed)],
        ["minimum_gap_surplus", "Minimum gap / surplus", () => isValidNumber(formState.minimum_gap_surplus)],
        ["stable_gap_surplus", "Stable gap / surplus", () => isValidNumber(formState.stable_gap_surplus)],
        ["growth_gap_surplus", "Growth gap / surplus", () => isValidNumber(formState.growth_gap_surplus)],
      ],
      [
        ...(formState.has_budget_for_role
          ? [
              [
                "monthly_gross_salary",
                "Monthly gross salary",
                () => isValidNumber(formState.monthly_gross_salary),
              ],
              ["nbr_of_months", "Number of months", () => isValidNumber(formState.nbr_of_months)],
              ["total_salary", "Total salary", () => isValidNumber(formState.total_salary)],
              [
                "budget_details",
                "Budget entries",
                () => {
                  const rows = formState.budget_details || [];
                  if (!rows.some(isBudgetEntryComplete)) return false;
                  return rows.every((row) => {
                    const started =
                      String(row.project_name || "").trim() !== "" ||
                      String(row.budget_line || "").trim() !== "" ||
                      (row.amount !== "" && row.amount != null) ||
                      String(row.budget_confirmed_until || "").trim() !== "";
                    return !started || isBudgetEntryComplete(row);
                  });
                },
              ],
              [
                "available_budget",
                "Available budget",
                () => isValidNumber(formState.available_budget),
              ],
            ]
          : []),
      ],
      [
        ["why_role_is_needed", "Why role is needed", () => isFilled(formState.why_role_is_needed)],
        [
          "evidence_reference_score",
          "Evidence reference score (1–3)",
          () => {
            const n = Number(formState.evidence_reference_score);
            return !Number.isNaN(n) && n >= 1 && n <= 3;
          },
        ],
        ["evidence_reference", "Evidence reference", () => isFilled(formState.evidence_reference)],
        [
          "risk_if_role_is_not_available_score",
          "Risk rating (1–3)",
          () => {
            const n = Number(formState.risk_if_role_is_not_available_score);
            return !Number.isNaN(n) && n >= 1 && n <= 3;
          },
        ],
        [
          "risk_if_role_is_not_available",
          "Risk if role is not available",
          () => isFilled(formState.risk_if_role_is_not_available),
        ],
        [
          "can_this_role_be_merged_or_shared",
          "Can this role be merged or shared",
          () =>
            formState.can_this_role_be_merged_or_shared === "Yes" ||
            formState.can_this_role_be_merged_or_shared === "No",
        ],
        [
          "merged_or_shared_details",
          formState.can_this_role_be_merged_or_shared === "Yes"
            ? "Which other role can be merged"
            : formState.can_this_role_be_merged_or_shared === "No"
            ? "Why it cannot be merged"
            : "Merge / share explanation",
          () => isFilled(formState.merged_or_shared_details),
        ],
      ],
      [
        ["total_score", "Total score", () => isValidNumber(formState.total_score)],
        ["criticality_category", "Criticality", () => isFilled(formState.criticality_category)],
        [
          "hod_requested_action_recommendation",
          "HoD Requested Action / Recommendation",
          () => isFilled(formState.hod_requested_action_recommendation),
        ],
      ],
    ];

    return (checks[index] || [])
      .filter(([, , ok]) => !ok())
      .map(([, label]) => label);
  };

  const isStepComplete = (index, formOverride = form, budgetDecisionOverride = budgetDecisionMade) => {
    if (index === 2 && !budgetDecisionOverride) return false;
    return getStepMissingFields(index, formOverride, budgetDecisionOverride).length === 0;
  };

  const isFormFullyComplete = (
    formOverride = form,
    budgetDecisionOverride = budgetDecisionMade
  ) =>
    STEPS.every((_, i) => isStepComplete(i, formOverride, budgetDecisionOverride));

  const maxReachableStep = useMemo(() => {
    let max = 0;
    for (let i = 0; i < STEPS.length; i += 1) {
      if (!isStepComplete(i)) break;
      max = Math.min(i + 1, STEPS.length - 1);
    }
    return max;
  }, [form, budgetDecisionMade]);

  const validateStep = (index, { silent = false, formOverride = form } = {}) => {
    const missing = getStepMissingFields(index, formOverride);
    const missingKeys = getStepMissingFieldKeys(index, formOverride);
    if (missing.length === 0) {
      if (index === step) setFieldErrors(new Set());
      return true;
    }
    if (!silent) {
      const preview = `${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`;
      const message =
        index === step
          ? `Please fill required fields: ${preview}`
          : `Complete step ${index + 1} first: ${preview}`;
      cogoToast.error(message, { hideAfter: 4 });
      if (index === step) setFieldErrors(new Set(missingKeys));
    }
    return false;
  };

  const saveProgress = ({
    formOverride = form,
    budgetDecisionOverride = budgetDecisionMade,
    advanceTo = null,
    finalize = false,
    returnToList = false,
    forceCompleted = false,
  } = {}) => {
    const derived = withDerivedGapsAndScore(formOverride);
    const payload = buildPayload(derived);
    delete payload.committee_decision;
    delete payload.comments;

    const fullyComplete = isFormFullyComplete(derived, budgetDecisionOverride);
    payload.status =
      forceCompleted || fullyComplete ? SUCCESS_RESULT : DEFAULT_RESULT;

    if (finalize && !fullyComplete && !forceCompleted) {
      cogoToast.error("Please complete all steps before submitting.", { hideAfter: 4 });
      return Promise.resolve(false);
    }

    setSaving(true);
    const savedId = draftId || id;
    const request = savedId
      ? updateWorkforcePlanningApi({ id: savedId, ...payload })
      : createWorkforcePlanningApi(payload);

    return request
      .then((res) => {
        setSaving(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const nextId = res.data.data?.id || savedId;
          if (nextId && nextId !== draftId) {
            setDraftId(nextId);
          }
          if (returnToList) {
            cogoToast.success(
              res.data.message ||
                (forceCompleted
                  ? "Record saved as completed"
                  : "Record saved"),
              { hideAfter: 3 }
            );
            history.push("/hrm/workforce-planning");
            return true;
          }
          if (finalize) {
            cogoToast.success(
              res.data.message || (isEdit || savedId ? "Updated successfully" : "Created successfully"),
              { hideAfter: 3 }
            );
            if ((isEdit || savedId) && nextId) {
              history.push(`/hrm/workforce-planning/details/${nextId}`);
            } else {
              history.push("/hrm/workforce-planning");
            }
          } else {
            cogoToast.success(res.data.message || "Progress saved", { hideAfter: 2 });
            if (advanceTo != null) {
              setStep(advanceTo);
            }
          }
          return true;
        }
        cogoToast.error(res.data?.message || "Save failed", { hideAfter: 5 });
        return false;
      })
      .catch((err) => {
        setSaving(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong. Please try again.",
          { hideAfter: 5 }
        );
        return false;
      });
  };

  const goNext = () => {
    if (step === 0) {
      if (!validateStep(step)) return;
      if (!form.critically_need_role) {
        cogoToast.error("Please confirm whether you critically need this role.", {
          hideAfter: 4,
        });
        return;
      }
      if (form.critically_need_role === "no") {
        setStaffingInputs({
          stable_staff_needed: form.stable_staff_needed || "",
          growth_staff_needed: form.growth_staff_needed || "",
        });
        setStaffingModal(true);
        return;
      }
      saveProgress({
        advanceTo: Math.min(step + 1, STEPS.length - 1),
      });
      return;
    }
    if (step === 2 && !form.has_budget_for_role) {
      skipBudgetAndContinue();
      return;
    }
    if (!validateStep(step)) return;
    saveProgress({
      advanceTo: Math.min(step + 1, STEPS.length - 1),
    });
  };

  const goBack = () => {
    setFieldErrors(new Set());
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = (index) => {
    if (index === step) return;
    if (index < step) {
      setStep(index);
      return;
    }
    // Only allow moving forward one reachable completed path; never validate the target step as a blocker
    if (index > maxReachableStep) {
      const firstIncomplete = STEPS.findIndex((_, i) => !isStepComplete(i));
      const blockedAt = firstIncomplete === -1 ? step : firstIncomplete;
      if (blockedAt !== step) {
        validateStep(blockedAt);
        setStep(blockedAt);
      } else {
        validateStep(blockedAt);
      }
      return;
    }
    for (let i = 0; i < index; i += 1) {
      if (!validateStep(i, { silent: true })) {
        validateStep(i);
        setStep(i);
        return;
      }
    }
    setStep(index);
  };

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  const handleSubmit = (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!isLastStep) {
      return;
    }
    for (let i = 0; i < STEPS.length; i += 1) {
      if (!validateStep(i)) {
        setStep(i);
        return;
      }
    }
    saveProgress({ finalize: true });
  };

  if (loading) {
    return (
      <div className="container-xl mt-4 text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="container-xl mt-4 mb-5 wfp-wizard">
      <div className="wfp-wizard__header">
        <div>
          <h1 className="wfp-wizard__title">
            {isEdit ? "Edit Workforce Planning" : "Create Workforce Planning"}
          </h1>
          <p className="wfp-wizard__subtitle">
            Step {step + 1} of {STEPS.length} · {currentStep.title}
          </p>
        </div>
        <Button
          className="wfp-btn"
          color="secondary"
          outline
          tag={Link}
          to="/hrm/workforce-planning"
        >
          Back to list
        </Button>
      </div>

      <div className="wfp-context">
        <div className="wfp-context__card">
          <div className="wfp-context__label">Department</div>
          <div className="wfp-context__value">{departmentLabel}</div>
        </div>
        <div className="wfp-context__card">
          <div className="wfp-context__label">Role</div>
          <div className="wfp-context__value">{roleLabel}</div>
        </div>
        <div className="wfp-context__card">
          <div className="wfp-context__label">Office</div>
          <div className="wfp-context__value">{officeLabel}</div>
          {form.area ? <div className="wfp-context__meta">Area · {form.area}</div> : null}
        </div>
      </div>

      <div className="wfp-steps">
        {STEPS.map((item, index) => {
          const locked = index > maxReachableStep && index !== step;
          const done = isStepComplete(index);
          return (
            <button
              key={item.key}
              type="button"
              className={`wfp-step${index === step ? " is-active" : ""}${
                done && index !== step ? " is-done" : ""
              }${locked ? " is-locked" : ""}`}
              onClick={() => goToStep(index)}
              disabled={locked}
              title={
                locked
                  ? "Complete the previous step before continuing"
                  : item.title
              }
            >
              <span className="wfp-step__index">{index + 1}</span>
              <span className="wfp-step__title">{item.title}</span>
            </button>
          );
        })}
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (isLastStep) {
            handleSubmit(e);
          }
        }}
        onKeyDown={(e) => {
          // Prevent Enter in inputs from submitting until user clicks Create
          if (e.key === "Enter" && e.target?.tagName !== "TEXTAREA") {
            e.preventDefault();
            if (!isLastStep) {
              goNext();
            }
          }
        }}
      >
        <div className="wfp-panel">
          <div className="wfp-panel__banner">
            <h3>{currentStep.title}</h3>
            <p>{currentStep.description}</p>
          </div>

          <div className="wfp-panel__body">
            {step === 0 && (
              <>
              <Row>
                <Col md={6}>
                  <Field label="Department">
                    <div className={fieldErrors.has("department_id") ? "wfp-select-error" : ""}>
                    <Select
                      className="wfp-select"
                      classNamePrefix="react-select"
                      isClearable
                      isLoading={loadingRoles}
                      options={departmentOptions}
                      value={selectedDepartment}
                      onChange={handleDepartmentChange}
                      placeholder={
                        loadingRoles ? "Loading departments..." : "Select department"
                      }
                      noOptionsMessage={() =>
                        loadingRoles ? "Loading..." : "No departments found"
                      }
                      {...SELECT_MENU_PORTAL_PROPS}
                    />
                    </div>
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Role / Designation">
                    <div className={fieldErrors.has("role_id") ? "wfp-select-error" : ""}>
                    <Select
                      className="wfp-select"
                      classNamePrefix="react-select"
                      isClearable
                      isLoading={loadingRoles}
                      isDisabled={!form.department_id}
                      options={roleOptions}
                      value={selectedRole}
                      onChange={(opt) => {
                        setForm((prev) =>
                          withDerivedGapsAndScore({
                            ...prev,
                            role_id: opt?.value || "",
                            critically_need_role: "",
                          })
                        );
                        setFieldErrors((prev) => { const n = new Set(prev); n.delete("role_id"); return n; });
                      }}
                      placeholder={
                        !form.department_id
                          ? "Select a department first"
                          : loadingRoles
                          ? "Loading designations..."
                          : "Select staff designation"
                      }
                      noOptionsMessage={() =>
                        form.department_id
                          ? "No roles found for this department"
                          : "Select a department first"
                      }
                      {...SELECT_MENU_PORTAL_PROPS}
                    />
                    </div>
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Area">
                    <div className={fieldErrors.has("area") ? "wfp-select-error" : ""}>
                    <Select
                      className="wfp-select"
                      classNamePrefix="react-select"
                      isClearable
                      options={AREA_OPTIONS}
                      value={selectedArea}
                      onChange={handleAreaChange}
                      placeholder="Select area (SC / PL / SL)"
                      {...SELECT_MENU_PORTAL_PROPS}
                    />
                    </div>
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Office">
                    <div className={fieldErrors.has("office") ? "wfp-select-error" : ""}>
                    <Select
                      className="wfp-select"
                      classNamePrefix="react-select"
                      isClearable
                      isLoading={loadingOffices}
                      isDisabled={!form.area}
                      options={officeOptions}
                      value={selectedOffice}
                      onChange={handleOfficeChange}
                      placeholder={
                        !form.area
                          ? "Select an area first"
                          : loadingOffices
                          ? "Loading offices..."
                          : "Select office"
                      }
                      noOptionsMessage={() =>
                        form.area
                          ? "No offices found for this area"
                          : "Select an area first"
                      }
                      {...SELECT_MENU_PORTAL_PROPS}
                    />
                    </div>
                  </Field>
                </Col>
              </Row>

              {form.department_id &&
                form.role_id &&
                form.area &&
                form.office && (
                  <div className="wfp-critical-need mt-3">
                    <div className="wfp-critical-need__question">
                      Do you critically need the <strong>{roleLabel}</strong> in{" "}
                      <strong>{officeLabel}</strong>?
                    </div>
                    <div className="wfp-critical-need__options">
                      <label
                        className={`wfp-radio-card${form.critically_need_role === "yes" ? " selected-yes" : ""}`}
                        htmlFor="critically_need_yes"
                      >
                        <input
                          id="critically_need_yes"
                          type="radio"
                          name="critically_need_role"
                          checked={form.critically_need_role === "yes"}
                          onChange={() =>
                            setForm((prev) =>
                              withDerivedGapsAndScore({
                                ...prev,
                                critically_need_role: "yes",
                              })
                            )
                          }
                          disabled={saving}
                        />
                        <span className="wfp-radio-card__label">Yes</span>
                      </label>
                      <label
                        className={`wfp-radio-card${form.critically_need_role === "no" ? " selected-no" : ""}`}
                        htmlFor="critically_need_no"
                      >
                        <input
                          id="critically_need_no"
                          type="radio"
                          name="critically_need_role"
                          checked={form.critically_need_role === "no"}
                          onChange={() =>
                            setForm((prev) =>
                              withDerivedGapsAndScore({
                                ...prev,
                                critically_need_role: "no",
                              })
                            )
                          }
                          disabled={saving}
                        />
                        <span className="wfp-radio-card__label">No</span>
                      </label>
                    </div>
                    <div className="wfp-hint mt-2">
                      After scoring is available, Critically needed is set automatically when
                      overall score is above 70 (Yes) or 70 and below (No).
                    </div>
                    {form.critically_need_role === "no" ? (
                      <div className="wfp-hint mt-2">
                        Continue will save this record as completed and return you to the
                        list. Completed records cannot be edited.
                      </div>
                    ) : null}
                  </div>
                )}
              </>
            )}

            {step === 1 && (
              <div className="wfp-staffing">
                <div className="wfp-staffing__current mb-3">
                  <Field label="Current staff count">
                    <Input
                      className={errCls("current_staff_count")}
                      type="number"
                      name="current_staff_count"
                      value={form.current_staff_count}
                      onChange={handleChange}
                    />
                  </Field>
                  <div className="wfp-hint">
                    Enter current headcount, then set staff needed for each portfolio stage below.
                  </div>
                </div>

                <div className="wfp-stage-grid">
                  <div className="wfp-stage-card wfp-stage-card--stress">
                    <div className="wfp-stage-card__header">
                      <span className="wfp-stage-card__badge">Stress</span>
                      <span className="wfp-stage-card__range">Portfolio &lt;8m</span>
                    </div>
                    <div className="wfp-stage-card__title">Minimum staff needed</div>
                    <p className="wfp-stage-card__desc">
                      Bare minimum team required to keep operations running.
                    </p>
                    <Field label="Staff needed">
                      <Input
                        className={errCls("minimum_staff_needed")}
                        type="number"
                        name="minimum_staff_needed"
                        value={form.minimum_staff_needed}
                        onChange={handleChange}
                      />
                    </Field>
                    <Field label="Gap / surplus" hint="Auto: current − minimum">
                      <Input
                        className="wfp-input"
                        type="number"
                        name="minimum_gap_surplus"
                        value={form.minimum_gap_surplus}
                        readOnly
                      />
                    </Field>
                  </div>

                  <div className="wfp-stage-card wfp-stage-card--stable">
                    <div className="wfp-stage-card__header">
                      <span className="wfp-stage-card__badge">Stable</span>
                      <span className="wfp-stage-card__range">Portfolio 8m–12m</span>
                    </div>
                    <div className="wfp-stage-card__title">Stable staff needed</div>
                    <p className="wfp-stage-card__desc">
                      Team size required to support normal operations efficiently.
                    </p>
                    <Field label="Staff needed">
                      <Input
                        className={errCls("stable_staff_needed")}
                        type="number"
                        name="stable_staff_needed"
                        value={form.stable_staff_needed}
                        onChange={handleChange}
                      />
                    </Field>
                    <Field label="Gap / surplus" hint="Auto: current − stable">
                      <Input
                        className="wfp-input"
                        type="number"
                        name="stable_gap_surplus"
                        value={form.stable_gap_surplus}
                        readOnly
                      />
                    </Field>
                  </div>

                  <div className="wfp-stage-card wfp-stage-card--growth">
                    <div className="wfp-stage-card__header">
                      <span className="wfp-stage-card__badge">Growth</span>
                      <span className="wfp-stage-card__range">Portfolio &gt;12m</span>
                    </div>
                    <div className="wfp-stage-card__title">Growth staff needed</div>
                    <p className="wfp-stage-card__desc">
                      Additional positions planned to support business growth and expansion once approved.
                    </p>
                    <Field label="Staff needed">
                      <Input
                        className={errCls("growth_staff_needed")}
                        type="number"
                        name="growth_staff_needed"
                        value={form.growth_staff_needed}
                        onChange={handleChange}
                      />
                    </Field>
                    <Field label="Gap / surplus" hint="Auto: current − growth">
                      <Input
                        className="wfp-input"
                        type="number"
                        name="growth_gap_surplus"
                        value={form.growth_gap_surplus}
                        readOnly
                      />
                    </Field>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="wfp-budget-step">
                <div className="wfp-budget-toggle mb-3">
                  <FormGroup check className="mb-0 wfp-budget-check">
                    <Input
                      id="has_budget_for_role"
                      type="checkbox"
                      className="wfp-budget-check__input"
                      checked={Boolean(form.has_budget_for_role)}
                      onChange={handleHasBudgetChange}
                      disabled={saving}
                    />
                    <Label check for="has_budget_for_role" className="wfp-budget-check__label">
                      Do you have budget for this role ({roleLabel})?
                    </Label>
                  </FormGroup>
                  {!form.has_budget_for_role && (
                    <div className="wfp-hint mt-2">
                      Leave unchecked if there is no confirmed budget. Continue will skip to
                      Justification.
                    </div>
                  )}
                </div>

                <div
                  className={`wfp-budget-step__content${
                    !form.has_budget_for_role ? " is-dimmed" : ""
                  }`}
                >
                  <fieldset
                    disabled={!form.has_budget_for_role}
                    className="wfp-budget-fieldset"
                  >
                    <Row>
                      <Col md={4}>
                        <Field label="Monthly gross salary">
                          <Input
                            className={errCls("monthly_gross_salary")}
                            type="number"
                            step="0.01"
                            name="monthly_gross_salary"
                            value={form.monthly_gross_salary}
                            onChange={handleChange}
                          />
                        </Field>
                      </Col>
                      <Col md={4}>
                        <Field label="Number of months">
                          <Input
                            className={errCls("nbr_of_months")}
                            type="number"
                            min={1}
                            name="nbr_of_months"
                            value={form.nbr_of_months}
                            onChange={handleChange}
                          />
                        </Field>
                      </Col>
                      <Col md={4}>
                        <Field
                          label={
                            <>
                              Total salary{" "}
                              <Badge color="light" className="text-dark">
                                auto
                              </Badge>
                            </>
                          }
                          hint="Monthly gross salary × number of months"
                        >
                          <Input
                            className="wfp-input"
                            type="number"
                            step="0.01"
                            name="total_salary"
                            value={form.total_salary}
                            readOnly
                          />
                        </Field>
                      </Col>
                    </Row>

                    {(form.budget_details || []).map((entry, index) => (
                      <div key={`budget-entry-${index}`} className="wfp-budget-entry mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="fw-semibold">Budget entry {index + 1}</div>
                          {form.has_budget_for_role &&
                            (form.budget_details || []).length > 1 && (
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                type="button"
                                onClick={() => removeBudgetEntry(index)}
                              >
                                <FaTrash className="me-1" /> Remove
                              </Button>
                            )}
                        </div>
                        <Row>
                          <Col md={3}>
                            <Field label="Project name">
                              <Input
                                className="wfp-input"
                                value={entry.project_name}
                                onChange={(e) =>
                                  handleBudgetEntryChange(index, "project_name", e.target.value)
                                }
                              />
                            </Field>
                          </Col>
                          <Col md={3}>
                            <Field label="Budget line">
                              <Input
                                className="wfp-input"
                                value={entry.budget_line}
                                onChange={(e) =>
                                  handleBudgetEntryChange(index, "budget_line", e.target.value)
                                }
                              />
                            </Field>
                          </Col>
                          <Col md={3}>
                            <Field label="Amount">
                              <Input
                                className="wfp-input"
                                type="number"
                                step="0.01"
                                value={entry.amount}
                                onChange={(e) =>
                                  handleBudgetEntryChange(index, "amount", e.target.value)
                                }
                              />
                            </Field>
                          </Col>
                          <Col md={3}>
                            <Field label="Budget confirmed until">
                              <Input
                                className="wfp-input"
                                type="date"
                                value={entry.budget_confirmed_until}
                                onChange={(e) =>
                                  handleBudgetEntryChange(
                                    index,
                                    "budget_confirmed_until",
                                    e.target.value
                                  )
                                }
                              />
                            </Field>
                          </Col>
                        </Row>
                      </div>
                    ))}

                    {form.has_budget_for_role && (
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        type="button"
                        className="mb-3"
                        onClick={addBudgetEntry}
                      >
                        <FaPlus className="me-1" /> Add budget entry
                      </Button>
                    )}

                    <Row className="mt-2">
                      <Col md={4}>
                        <Field
                          label={
                            <>
                              Available budget{" "}
                              <Badge color="light" className="text-dark">
                                auto
                              </Badge>
                            </>
                          }
                          hint="Sum of amounts from budget entries"
                        >
                          <Input
                            className="wfp-input"
                            type="number"
                            step="0.01"
                            name="available_budget"
                            value={form.available_budget}
                            readOnly
                          />
                        </Field>
                      </Col>
                      <Col md={4}>
                        <Field
                          label={
                            <>
                              Budget surplus / gap{" "}
                              <Badge color="light" className="text-dark">
                                auto
                              </Badge>
                            </>
                          }
                          hint="Available budget − total salary"
                        >
                          <Input
                            className="wfp-input"
                            type="number"
                            step="0.01"
                            name="budget_surplus_gap"
                            value={form.budget_surplus_gap}
                            readOnly
                          />
                        </Field>
                      </Col>
                    </Row>
                  </fieldset>
                </div>
              </div>
            )}

            {step === 3 && (
              <Row>
                <Col md={12}>
                  <Field label="Why role is needed">
                    <Input
                      className={errCls("why_role_is_needed")}
                      type="textarea"
                      rows={3}
                      name="why_role_is_needed"
                      value={form.why_role_is_needed}
                      onChange={handleChange}
                    />
                  </Field>
                </Col>

                <Col md={4}>
                  <Field
                    label="Evidence reference score"
                    hint="1 = Low, 2 = Moderate, 3 = High — how strong is the evidence reference?"
                  >
                    <Input
                      className={errCls("evidence_reference_score")}
                      type="select"
                      name="evidence_reference_score"
                      value={form.evidence_reference_score}
                      onChange={handleChange}
                    >
                      <option value="">Select 1–3…</option>
                      {EVIDENCE_RATING_OPTIONS_1_TO_3.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Field>
                </Col>
                <Col md={8}>
                  <Field
                    label="Evidence reference"
                    hint="Explain the evidence reference for the score above."
                  >
                    <Input
                      className={errCls("evidence_reference")}
                      type="textarea"
                      rows={3}
                      name="evidence_reference"
                      value={form.evidence_reference}
                      onChange={handleChange}
                    />
                  </Field>
                </Col>

                <Col md={4}>
                  <Field
                    label="Risk if role is not available (score)"
                    hint="1 = Low risk, 2 = Moderate risk, 3 = High risk."
                  >
                    <Input
                      className={errCls("risk_if_role_is_not_available_score")}
                      type="select"
                      name="risk_if_role_is_not_available_score"
                      value={form.risk_if_role_is_not_available_score}
                      onChange={handleChange}
                    >
                      <option value="">Select 1–3…</option>
                      {RATING_OPTIONS_1_TO_3.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Field>
                </Col>
                <Col md={8}>
                  <Field
                    label="Risk if role is not available"
                    hint="Explain the risk for the score above."
                  >
                    <Input
                      className={errCls("risk_if_role_is_not_available")}
                      type="textarea"
                      rows={3}
                      name="risk_if_role_is_not_available"
                      value={form.risk_if_role_is_not_available}
                      onChange={handleChange}
                    />
                  </Field>
                </Col>

                <Col md={4}>
                  <Field label="Can this role be merged or shared?">
                    <Input
                      className={errCls("can_this_role_be_merged_or_shared")}
                      type="select"
                      name="can_this_role_be_merged_or_shared"
                      value={form.can_this_role_be_merged_or_shared}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((prev) =>
                          withDerivedGapsAndScore({
                            ...prev,
                            can_this_role_be_merged_or_shared: value,
                            merged_or_shared_details:
                              value === prev.can_this_role_be_merged_or_shared
                                ? prev.merged_or_shared_details
                                : "",
                          })
                        );
                      }}
                    >
                      <option value="">Select…</option>
                      {ROLE_MERGED_OR_SHARED_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Input>
                  </Field>
                </Col>
                <Col md={8}>
                  {form.can_this_role_be_merged_or_shared === "Yes" ? (
                    <Field
                      label="Which other role can be merged with?"
                      hint="Name the role(s) this position can be merged or shared with."
                    >
                      <Input
                        className={errCls("merged_or_shared_details")}
                        type="textarea"
                        rows={3}
                        name="merged_or_shared_details"
                        value={form.merged_or_shared_details}
                        onChange={handleChange}
                        placeholder="e.g. Share duties with Programme Officer"
                      />
                    </Field>
                  ) : form.can_this_role_be_merged_or_shared === "No" ? (
                    <Field
                      label="Why it cannot be merged"
                      hint="Explain why this role cannot be merged or shared."
                    >
                      <Input
                        className={errCls("merged_or_shared_details")}
                        type="textarea"
                        rows={3}
                        name="merged_or_shared_details"
                        value={form.merged_or_shared_details}
                        onChange={handleChange}
                        placeholder="Explain why merging is not possible"
                      />
                    </Field>
                  ) : (
                    <Field label="Merge / share details">
                      <Input
                        className="wfp-input"
                        type="textarea"
                        rows={3}
                        disabled
                        placeholder="Select Yes or No first"
                        value=""
                        readOnly
                      />
                    </Field>
                  )}
                </Col>
              </Row>
            )}

            {step === 4 && (
              <>
                <div className="wfp-score-review mb-4 d-none">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <h5 className="mb-0">Scoring summary</h5>
                    <Badge color="primary" pill>
                      Total {form.total_score ?? 0} / {AUTO_SCORE_MAX}
                    </Badge>
                  </div>
                  <div className="table-responsive">
                    <Table bordered hover className="align-middle mb-0 wfp-score-table">
                      <thead>
                        <tr>
                          <th>Criteria</th>
                          <th>Detail</th>
                          <th style={{ width: 110 }} className="text-end">
                            Points
                          </th>
                          <th style={{ width: 90 }} className="text-end">
                            Max
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {computeAutoScores(form).rows.map((row) => (
                          <tr key={row.key}>
                            <td>{row.criteria}</td>
                            <td className="text-muted">{row.detail}</td>
                            <td className="text-end fw-semibold">{row.points}</td>
                            <td className="text-end text-muted">{row.maxPoints}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan={2}>Total</th>
                          <th className="text-end">{form.total_score ?? 0}</th>
                          <th className="text-end">{AUTO_SCORE_MAX}</th>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                  <div className="wfp-hint mt-2">
                    Role need Yes = 1. Staffing stress shortage = min(|gap|, 4). Budget: no
                    gap/surplus = 45; shortfall &lt;10% = 35, 10–50% = 25, &gt;50% = 10; no
                    budget = 0. Justification up to 50 (why 10 · evidence×4 · risk×3 ·
                    merge/share 5).
                  </div>
                </div>

                <Row>
                  <Col md={4} className="d-none">
                    <Field
                      label={
                        <>
                          Criticality Category{" "}
                          <Badge color="light" className="text-dark">
                            auto
                          </Badge>
                        </>
                      }
                      hint="Derived automatically from the total score (out of 100)"
                    >
                      <Input
                        className="wfp-input"
                        name="criticality_category"
                        value={form.criticality_category}
                        readOnly
                      />
                      <div className="wfp-hint mt-2 mb-0">
                        How this value is decided from the total score:
                        <ul className="mb-0 mt-1 ps-3">
                          <li>
                            <strong>75–100</strong> → Mission Critical
                          </li>
                          <li>
                            <strong>55–74</strong> → Essential
                          </li>
                          <li>
                            <strong>35–54</strong> → Important / Flexible
                          </li>
                          <li>
                            <strong>0–34</strong> → Non-Critical / Optional
                          </li>
                        </ul>
                      </div>
                    </Field>
                  </Col>
                  <Col md={12}>
                    <Field label="HoD Requested Action / Recommendation">
                      <Input
                        className={errCls("hod_requested_action_recommendation")}
                        type="textarea"
                        rows={8}
                        name="hod_requested_action_recommendation"
                        value={form.hod_requested_action_recommendation}
                        onChange={handleChange}
                        placeholder="e.g. Recruit immediately"
                      />
                    </Field>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </div>

        <div className="wfp-footer">
          <Button
            className="wfp-btn"
            color="secondary"
            outline
            type="button"
            onClick={() => history.push("/hrm/workforce-planning")}
            disabled={saving}
          >
            Cancel
          </Button>

          <div className="wfp-footer__right">
            {step > 0 && (
              <Button
                className="wfp-btn"
                color="secondary"
                outline
                type="button"
                onClick={goBack}
                disabled={saving}
              >
                Back
              </Button>
            )}
            {!isLastStep ? (
              <div
                style={{ display: "inline-block" }}
                onClick={() => {
                  if (
                    step === 0 &&
                    (!isStepComplete(0) || !form.critically_need_role)
                  ) {
                    if (!form.critically_need_role) {
                      cogoToast.warn(
                        "Please select Yes or No to the critical need question before continuing.",
                        { hideAfter: 4 }
                      );
                    } else {
                      cogoToast.warn(
                        "Please fill in all required fields before continuing.",
                        { hideAfter: 4 }
                      );
                    }
                  }
                }}
              >
              <Button
                className="wfp-btn wfp-btn-primary"
                color="primary"
                type="button"
                onClick={goNext}
                disabled={
                  saving ||
                  (step === 0 &&
                    (!isStepComplete(0) || !form.critically_need_role))
                }
                style={
                  step === 0 && (!isStepComplete(0) || !form.critically_need_role)
                    ? { pointerEvents: "none" }
                    : {}
                }
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Saving…
                  </>
                ) : step === 0 && form.critically_need_role === "no" ? (
                  "Save & return to list"
                ) : form.has_budget_for_role || step !== 2 ? (
                  "Continue"
                ) : (
                  "Continue without budget"
                )}
              </Button>
              </div>
            ) : (
              <Button
                className="wfp-btn wfp-btn-primary"
                color="primary"
                type="button"
                onClick={handleSubmit}
                disabled={saving || !isStepComplete(step)}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Saving…
                  </>
                ) : isEdit ? (
                  "Update record"
                ) : (
                  "Create record"
                )}
              </Button>
            )}
          </div>
        </div>
      </Form>

      <Modal isOpen={staffingModal} toggle={() => setStaffingModal(false)} centered>
        <ModalHeader toggle={() => setStaffingModal(false)}>
          Staff Numbers Required
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Since this role is not critically needed, please provide the staffing numbers for planning purposes.
          </p>
          <FormGroup>
            <Label for="modal_stable_staff">
              <strong>Stable staff needed</strong>
              <small className="text-muted ms-2">(Portfolio 8m–12m)</small>
            </Label>
            <Input
              id="modal_stable_staff"
              type="number"
              min="0"
              value={staffingInputs.stable_staff_needed}
              onChange={(e) =>
                setStaffingInputs((prev) => ({ ...prev, stable_staff_needed: e.target.value }))
              }
              placeholder="Enter number"
            />
          </FormGroup>
          <FormGroup>
            <Label for="modal_growth_staff">
              <strong>Growth staff needed</strong>
              <small className="text-muted ms-2">(Portfolio &gt;12m)</small>
            </Label>
            <Input
              id="modal_growth_staff"
              type="number"
              min="0"
              value={staffingInputs.growth_staff_needed}
              onChange={(e) =>
                setStaffingInputs((prev) => ({ ...prev, growth_staff_needed: e.target.value }))
              }
              placeholder="Enter number"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setStaffingModal(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={
              saving ||
              staffingInputs.stable_staff_needed === "" ||
              staffingInputs.growth_staff_needed === ""
            }
            onClick={() => {
              const updatedForm = withDerivedGapsAndScore({
                ...form,
                critically_need_role: "no",
                stable_staff_needed: staffingInputs.stable_staff_needed,
                growth_staff_needed: staffingInputs.growth_staff_needed,
              });
              setForm(updatedForm);
              setStaffingModal(false);
              saveProgress({ formOverride: updatedForm, returnToList: true, forceCompleted: true });
            }}
          >
            {saving ? (
              <><Spinner size="sm" className="me-2" /> Saving…</>
            ) : (
              "Save & return to list"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default WorkforcePlanningForm;
